import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WordIcon from '../../../assets/icons/word';
import CrossIcon from '../../../assets/icons/cross';
import AppsMenu from '../../../assets/icons/apps_menu';
import { useHeaderContext } from '../../../context/HeaderContext';
import { useFooterContext } from '../../../context/FooterContext';
import PdfIcon from '../../../assets/icons/pdf';
import { format } from 'date-fns';
import { getColorByStep, Step } from '../../../helpers/stepHelper';
import TodayIcon from '@mui/icons-material/Today';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import { DocumentType, getDocumentByType } from '../../../helpers/documentHelper';
import StatusBadge from '../../../components/ui/StatusBadge';
import { getColorByProcurementType, getLabelByProcurementType } from '../../../helpers/procurementHelper';
import { Button } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

export default function SidebarDetails({
  procurement,
  children,
}) {
  const navigate = useNavigate();
  const { headerHeight } = useHeaderContext();
  const { footerHeight } = useFooterContext();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1024);
  const [isOpen, setIsOpen] = useState(isSmallScreen ? false : true);
  const detailsBarRef = useRef(null);

  const specificationsDoc = getDocumentByType(procurement.documents, [DocumentType.SPECIFICATIONS]);
  const approbations = procurement.multipleApprobation?.map(user => user.fullname);

  const toggle = () => setIsOpen(!isOpen);
  const hide = () => setIsOpen(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 1024);
      setIsOpen(window.innerWidth >= 1024);
    };

    const handleClickOutside = (event) => {
      if (isSmallScreen && detailsBarRef.current && !detailsBarRef.current.contains(event.target)) {
        hide();
      }
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSmallScreen]);

  return (
    <>
      <aside ref={detailsBarRef} style={{ paddingTop: `${headerHeight}px`, paddingBottom: `${footerHeight}px` }} className={`fixed h-full bottom-0 right-0 z-30 w-72 transition-transform duration-300 ease-in-out transform ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="h-full p-4 overflow-y-auto bg-gray-50 flex flex-col justify-between">
          <div className="flex flex-col space-y-4 font-medium">
            {isSmallScreen
              && (
                <button className="w-10 h-auto mx-4 self-end" onClick={toggle}>
                  <span><CrossIcon /></span>
                </button>
              )}
            <div className="self-center">
              <Button
                variant="contained"
                color="primary"
                disableElevation
                onClick={() => navigate("/")}
                startIcon={<ArrowBackIosNewIcon />}
              >
                Retour à la liste
              </Button>
            </div>
            <div className="flex flex-col space-y-2">
              <span className="font-bold text-xl">
                Initiateur
              </span>
              <span>{procurement.initiator.fullname}</span>
            </div>
            <div className="flex flex-col space-y-2">
              <span className="font-bold text-xl">Cahier des charges</span>
              {specificationsDoc
                ? (
                  <div
                    className="w-10 h-auto cursor-pointer text-blue-600 flex items-center gap-2"
                    onClick={() => window.open(specificationsDoc.url, "_blank")}
                  >
                    {specificationsDoc.name.endsWith(".pdf")
                      ? <PdfIcon />
                      : <WordIcon />}
                  </div>
                )
                : procurement.isGenerating
                  ? (
                    <p className="text-gray-500">
                      Document en cours de génération{" "}
                      <span className="text-sm italic">
                        (veuillez rafraîchir la page d’ici quelques secondes).
                      </span>
                    </p>
                  )
                  : (
                    <span className="text-gray-500">
                      Aucun document{" "}
                      <span className="text-sm italic">
                        (il sera généré après validation du formulaire)
                      </span>
                    </span>
                  )}
            </div>

            <div className="flex flex-col space-y-2 ">
              <span className="font-bold text-xl">
                Type de marché
              </span>
              <span>
                <StatusBadge text={getLabelByProcurementType(procurement.type)} color={getColorByProcurementType(procurement.type)} />
              </span>
            </div>
            <div className="flex flex-col space-y-2 ">
              <span className="font-bold text-xl">
                Statut
              </span>
              <div>
                <StatusBadge text={procurement.step.status} color={getColorByStep[procurement.step.name]} />
              </div>
            </div>
            {
              approbations?.length > 0
              && (
                <div className="flex flex-col space-y-2 ">
                  <span className="font-bold text-xl">
                    Approbation(s) reçue(s)
                  </span>
                  <ul className="list-disc">
                    {
                      approbations.map(username => (
                        <li key={username} className="ml-7 break-words">
                          {username}
                        </li>
                      ),
                      )
                    }
                  </ul>
                </div>
              )
            }
            {
              procurement.step.name === Step.END && (procurement.actualStartAt || procurement.actualEndAt) && (
                <div className="flex flex-col space-y-2 ">
                  <span className="font-bold text-xl">
                    Suivi
                  </span>
                  {
                    procurement.actualStartAt && (
                      <>
                        <span className="font-semibold">
                          Date de début
                        </span>
                        <div>
                          <TodayIcon /> {format(procurement.actualStartAt, "dd/MM/yyy")}
                        </div>
                      </>
                    )
                  }
                  {
                    procurement.actualEndAt && (
                      <>
                        <span className="font-semibold">
                          Date de fin
                        </span>
                        <div>
                          <InsertInvitationIcon /> {format(procurement.actualEndAt, "dd/MM/yyy")}
                        </div>
                      </>
                    )
                  }
                </div>
              )
            }
          </div>
          <div className="flex flex-col w-full space-y-4 pt-7">
            {children}
          </div>
        </div>
      </aside>
      {isSmallScreen
        && (
          <div className="fixed right-0">
            <button className="w-10 h-auto m-4 p-1 bg-gray-300 rounded-lg" onClick={toggle}>
              <span><AppsMenu /></span>
            </button>
          </div>
        )}
    </>
  );
};
